import { z } from 'zod';
import { STORY_GENRES, MAX_ENTRY_LENGTH, ADVENTURE_THEMES, MAX_ACTION_LENGTH } from '../types';

// ============================================
// Auth Schemas
// ============================================

export const emailSchema = z
  .string()
  .email('Invalid email format')
  .toLowerCase();

export const authRequestCodeSchema = z.object({
  email: emailSchema,
});

export const authVerifySchema = z.object({
  email: emailSchema,
  code: z.string().length(6, 'Code must be 6 digits'),
});

export const authAppleSchema = z.object({
  identityToken: z.string().min(1),
  /** Full name may only be provided by Apple on first sign-in; optional. */
  fullName: z.string().max(60).optional(),
});

export const authGoogleSchema = z.object({
  idToken: z.string().min(1),
});

// ============================================
// User Schemas
// ============================================

export const updateUserSchema = z.object({
  name: z.string().min(1).max(50).optional(),
  avatar: z.string().url().optional(),
});

export const updateProfileSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(30, 'Name must be at most 30 characters').optional(),
  avatar: z.string().optional(), // base64 or URL
  otaOptInBeta: z.boolean().optional(),
});

// ============================================
// Story Schemas
// ============================================

export const storyGenreSchema = z.enum(STORY_GENRES as [string, ...string[]]);

export const aiProviderSchema = z.enum(['xai', 'openai', 'anthropic']);

export const imageProviderSchema = z.enum(['openai', 'fal', 'xai']);

export const createStorySchema = z.object({
  genre: storyGenreSchema,
  maxParticipants: z.number().min(2).max(20),
  firstEntry: z
    .string()
    .min(10, 'Entry must be at least 10 characters')
    .max(MAX_ENTRY_LENGTH, `Entry must be at most ${MAX_ENTRY_LENGTH} characters`),
  creatorHint: z.string().max(128).optional(),
  showHintToParticipants: z.boolean().optional(),
  aiProvider: aiProviderSchema.optional(),
  aiModel: z.string().max(64).optional(),
});

export const joinStorySchema = z.object({
  name: z.string().min(1).max(50).optional(),
});

export const storyCodeSchema = z
  .string()
  .length(6, 'Story code must be 6 characters')
  .regex(/^[A-Z0-9]+$/, 'Invalid story code format');

// ============================================
// Entry Schemas
// ============================================

export const addEntrySchema = z.object({
  text: z
    .string()
    .min(10, 'Entry must be at least 10 characters')
    .max(MAX_ENTRY_LENGTH, `Entry must be at most ${MAX_ENTRY_LENGTH} characters`),
  previousEntryId: z.string().optional(), // For race condition check
});

// ============================================
// Anonymous Schemas
// ============================================

export const anonymousIdSchema = z
  .string()
  .uuid('Invalid anonymous ID format');

export const displayNameSchema = z
  .string()
  .min(1, 'Name is required')
  .max(30, 'Name must be at most 30 characters');

export const anonymousJoinSchema = z.object({
  anonymousId: anonymousIdSchema,
  displayName: displayNameSchema,
});

export const anonymousAddEntrySchema = z.object({
  anonymousId: anonymousIdSchema,
  text: z
    .string()
    .min(10, 'Entry must be at least 10 characters')
    .max(MAX_ENTRY_LENGTH, `Entry must be at most ${MAX_ENTRY_LENGTH} characters`),
});

export const anonymousIdBodySchema = z.object({
  anonymousId: anonymousIdSchema,
});

// ============================================
// Query Schemas
// ============================================

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const storyFilterSchema = z.object({
  status: z.enum(['active', 'completed', 'all']).default('all'),
});

// ============================================
// Type exports from schemas
// ============================================

export type AuthRequestCodeInput = z.infer<typeof authRequestCodeSchema>;
export type AuthVerifyInput = z.infer<typeof authVerifySchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type CreateStoryInput = z.infer<typeof createStorySchema>;
export type JoinStoryInput = z.infer<typeof joinStorySchema>;
export type AddEntryInput = z.infer<typeof addEntrySchema>;
export type PaginationInput = z.infer<typeof paginationSchema>;
export type StoryFilterInput = z.infer<typeof storyFilterSchema>;
export type AnonymousJoinInput = z.infer<typeof anonymousJoinSchema>;
export type AnonymousAddEntryInput = z.infer<typeof anonymousAddEntrySchema>;

// ============================================
// Adventure Schemas
// ============================================

export const adventureThemeSchema = z.enum(ADVENTURE_THEMES as [string, ...string[]]);

export const adventureCodeSchema = z
  .string()
  .length(6, 'Adventure code must be 6 characters')
  .regex(/^[A-Z0-9]+$/, 'Invalid adventure code format');

export const imageStyleSchema = z.enum(['manga', 'comic', 'realistic', 'watercolor', 'anime', 'oil_painting', 'sketch', 'pixel', 'digital_illustration']);

export const turnModeSchema = z.enum(['sequential', 'free', 'vote']);

export const characterModeSchema = z.enum(['predefined', 'custom', 'ai_assigned']);

export const adventureConfigSchema = z.object({
  moderation: z.boolean().optional(),
  maxRounds: z.number().min(5).max(30).optional(),
  turnMode: turnModeSchema.optional(),
  imageStyle: imageStyleSchema.optional(),
  characterMode: characterModeSchema.optional(),
});

export const adventureCharacterSchema = z.object({
  name: z.string().min(1).max(50),
  description: z.string().max(500).optional(),
  avatarUrl: z.string().url().optional(),
});

export const npcRoleSchema = z.enum(['npc', 'villain', 'ally', 'mysterious']);

export const adventureNPCSchema = z.object({
  name: z.string().min(1).max(50),
  description: z.string().max(300).optional(),
  role: npcRoleSchema.optional(),
});

export const createAdventureSchema = z.object({
  theme: adventureThemeSchema,
  setting: z.string().max(500).optional(),
  npcs: z.array(adventureNPCSchema).max(10).optional(),
  config: adventureConfigSchema.optional(),
  maxParticipants: z.number().min(1).max(6).optional(),
  character: adventureCharacterSchema.optional(),
  // AI model selection (admin only)
  aiProvider: aiProviderSchema.optional(),
  aiModel: z.string().max(64).optional(),
  // Image provider selection (admin only)
  imageProvider: imageProviderSchema.optional(),
  imageModel: z.string().max(64).optional(),
});

export const joinAdventureSchema = z.object({
  character: adventureCharacterSchema.optional(),
  displayName: z.string().min(1).max(30).optional(),
});

export const adventureActionSchema = z.object({
  text: z
    .string()
    .min(1, 'Action is required')
    .max(MAX_ACTION_LENGTH, `Action must be at most ${MAX_ACTION_LENGTH} characters`)
    .optional(),
  suggestionIndex: z.number().min(0).max(2).optional(),
}).refine(
  data => data.text !== undefined || data.suggestionIndex !== undefined,
  { message: 'Either text or suggestionIndex is required' }
);

// ============================================
// Comic Schemas
// ============================================

export const comicVisibilitySchema = z.enum(['private', 'unlisted', 'public']);

export const updateComicSchema = z.object({
  visibility: comicVisibilitySchema.optional(),
  title: z.string().min(1).max(100).optional(),
});

export const comicShareCodeSchema = z
  .string()
  .length(8, 'Share code must be 8 characters')
  .regex(/^[a-z0-9]+$/, 'Invalid share code format');

// ============================================
// Adventure Type Exports
// ============================================

export type CreateAdventureInput = z.infer<typeof createAdventureSchema>;
export type JoinAdventureInput = z.infer<typeof joinAdventureSchema>;
export type AdventureActionInput = z.infer<typeof adventureActionSchema>;
export type UpdateComicInput = z.infer<typeof updateComicSchema>;

// ============================================
// Reaction Schemas
// ============================================

export const reactionEmojiSchema = z.enum(['😂', '❤️', '😮', '😢', '🔥']);

export const reactionRequestSchema = z.object({
  emoji: reactionEmojiSchema.nullable(),
});

export type ReactionRequestInput = z.infer<typeof reactionRequestSchema>;

// ============================================
// Story Update Schemas
// ============================================

export const storyVisibilitySchema = z.enum(['public', 'unlisted']);

export const updateStorySchema = z.object({
  visibility: storyVisibilitySchema.optional(),
});

export type UpdateStoryInput = z.infer<typeof updateStorySchema>;

// ============================================
// Push Notification Schemas
// ============================================

export const devicePlatformSchema = z.enum(['ios', 'android']);

export const registerDeviceSchema = z.object({
  token: z.string().min(1).max(4096),
  platform: devicePlatformSchema,
  deviceId: z.string().min(1).max(256),
  enabled: z.boolean(),
});

export const unregisterDeviceSchema = z.object({
  deviceId: z.string().min(1).max(256),
});
